import { text, useLanguage, t, setLanguage, type Language } from './i18n';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { isSuperAdmin, type PortalUser } from './api';
import { FeedbackNotice } from './feedback';
import { useAuth } from './auth';
import { navigate, type Route } from './router';
import { prefetch } from './queries';
import { applyThemeMode, nextThemeMode, storedThemeMode, type ThemeMode } from './theme';
import { LanguageSwitch } from './shared/ui/LanguageSwitch';
import { CommandPalette } from './shared/ui/CommandPalette';
import { PromotionBanner } from './shared/ui/PromotionBanner';
import { formatUsd } from './utils';

type NavItem = {
  id: Route['id'];
  href: string;
  label: string;
  min: 'public' | 'standard' | 'admin';
  icon:
    | 'dashboard'
    | 'organization'
    | 'security'
    | 'endpoints'
    | 'keys'
    | 'models'
    | 'usage'
    | 'history'
    | 'support'
    | 'limits';
  soon?: boolean;
};

type MobileSlot = 'primary' | 'more' | 'hidden';

function buildNav(
  supportEnabled: boolean,
  billingSupportEnabled: boolean,
): Array<{ title: string; items: NavItem[] }> {
  // Group by the task domain, never by availability or access level.
  return [
    {
      title: '',
      items: [
        {
          id: 'home',
          href: '/',
          label: text('概览', 'Overview', '概要'),
          min: 'public',
          icon: 'dashboard',
        },
        {
          id: 'usage',
          href: '/usage',
          label: t('nav.usage_billing'),
          min: 'standard',
          icon: 'usage',
        },
        {
          id: 'history',
          href: '/request-history',
          label: t('history.request_history'),
          min: 'standard',
          icon: 'history',
        },
      ],
    },
    {
      title: text('API 管理', 'API management', 'API 管理'),
      items: [
        {
          id: 'keys',
          href: '/keys',
          label: t('dashboard.my_api_keys'),
          min: 'standard',
          icon: 'keys',
        },
        {
          id: 'models',
          href: '/models',
          label: t('nav.available_models'),
          min: 'standard',
          icon: 'models',
        },
        {
          id: 'endpoints',
          href: '/endpoints',
          label: t('endpoint.endpoints'),
          min: 'standard',
          icon: 'endpoints',
        },
        {
          id: 'home',
          href: '#',
          label: t('nav.my_rate_limits'),
          min: 'standard',
          icon: 'limits',
          soon: true,
        },
      ],
    },
    {
      title: text('设置', 'Settings', '設定'),
      items: [
        {
          id: 'team',
          href: '/team',
          label: t('endpoint.organization'),
          min: 'standard',
          icon: 'organization',
        },
        {
          id: 'security',
          href: '/security',
          label: t('nav.security'),
          min: 'public',
          icon: 'security',
        },
      ],
    },
    ...(supportEnabled || billingSupportEnabled
      ? [
          {
            title: text('帮助', 'Help', 'ヘルプ'),
            items: [
              {
                id: 'support' as const,
                href: '/support',
                label: text('支持', 'Support', 'サポート'),
                min: 'public' as const,
                icon: 'support' as const,
              },
            ],
          },
        ]
      : []),
  ];
}

const mobilePrimaryRoutes = new Set<Route['id']>(['home', 'history', 'keys', 'usage']);

// Pages without their own heading get the route title as the page title, so the
// desktop topbar does not need to repeat it.
const SHELL_TITLED_ROUTES = new Set(['keys', 'endpoints', 'team', 'security', 'support']);

export function AppLayout({ route, children }: { route: Route; children: ReactNode }) {
  const language = useLanguage();
  const { user, accessLevel, loading } = useAuth();
  const supportEnabled = user?.support_case_intake_enabled === true;
  const billingSupportEnabled =
    user?.billing_support_case_intake_enabled === true || Boolean(user?.billing_support_url);
  const nav = useMemo(
    () => buildNav(supportEnabled, billingSupportEnabled),
    [billingSupportEnabled, language, supportEnabled],
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const previousRoute = useRef(route.id);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => document.documentElement.dataset.sidebar === 'collapsed',
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => storedThemeMode());
  const [avatarFailed, setAvatarFailed] = useState(false);
  const avatarFallback = (user?.email?.trim().charAt(0) || 'U').toUpperCase();
  const avatarUrl = user?.avatar_url?.trim() || '';
  // Standard is the normal state for customers, so only exceptions get a badge.
  const accessBadge = loading
    ? null
    : isSuperAdmin(user)
      ? t('nav.admin')
      : accessLevel === 'public'
        ? t('nav.public')
        : null;
  const accessBadgeClass = isSuperAdmin(user)
    ? 'badge-admin'
    : accessLevel === 'public'
      ? 'badge-public'
      : 'badge-standard';
  const accessRank = { public: 0, standard: 1, admin: 2 } as const;
  const isLocked = (item: NavItem) =>
    !item.soon && !loading && accessRank[accessLevel] < accessRank[item.min];
  const isBlocked = (item: NavItem) => item.soon || isLocked(item);
  const paletteDestinations = nav
    .flatMap((section) => section.items)
    .filter((item) => !isBlocked(item))
    .map((item) => ({ href: item.href, label: item.label }));
  const balance = user?.credit_balance_usd;
  const balanceLow = typeof balance === 'number' && balance < 1;

  useLayoutEffect(() => {
    if (previousRoute.current === route.id) return;
    previousRoute.current = route.id;
    const element = contentRef.current;
    if (!element || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animation = element.animate(
      [
        { opacity: 0.45, transform: 'translateY(6px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 200, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
    return () => animation.cancel();
  }, [route.id]);

  useEffect(() => {
    if (!menuOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        accountMenuRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [menuOpen]);

  useEffect(() => {
    document.body.className = 'portal-shell';
    document.body.dataset.page = route.id === 'endpointDetail' ? 'endpoints' : route.id;
    setMobileMoreOpen(false);
    setMenuOpen(false);
  }, [route]);

  useEffect(() => {
    applyThemeMode(themeMode);
  }, [themeMode]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatarUrl]);

  return (
    <>
      <aside className="sidebar">
        <div className="sidebar-header">
          <a
            className="sidebar-logo"
            href="/"
            onClick={handleNav}
            inert={sidebarCollapsed}
            aria-hidden={sidebarCollapsed}
          >
            <img src="/img/favicon.svg" alt="Antigma" />
            <span className="sidebar-logo-text">Antix Console</span>
          </a>
          <button
            id="sidebar-collapse"
            className="sidebar-collapse-btn"
            type="button"
            aria-label={
              sidebarCollapsed
                ? text('展开侧栏', 'Expand sidebar', 'サイドバーを展開')
                : text('收起侧栏', 'Collapse sidebar', 'サイドバーを折りたたむ')
            }
            title={
              sidebarCollapsed
                ? text('展开侧栏', 'Expand sidebar', 'サイドバーを展開')
                : text('收起侧栏', 'Collapse sidebar', 'サイドバーを折りたたむ')
            }
            aria-expanded={!sidebarCollapsed}
            onClick={() => {
              toggleSidebar();
              setSidebarCollapsed(document.documentElement.dataset.sidebar === 'collapsed');
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" />
            </svg>
          </button>
        </div>
        <nav className="sidebar-nav">
          {nav.map((section) => (
            <div className="nav-group" key={section.title}>
              {section.title && <p className="nav-group-label">{section.title}</p>}
              <div className="nav-group-line" />
              {section.items.map((item) => {
                const blocked = isBlocked(item);
                const locked = isLocked(item);
                const mobileSlot = mobileNavSlot(item);
                return (
                  <a
                    key={`${section.title}-${item.id}-${item.label}`}
                    href={item.href}
                    data-nav-id={item.soon ? undefined : item.id}
                    data-min-type={item.min}
                    aria-label={item.label}
                    aria-current={route.id === item.id && !item.soon ? 'page' : undefined}
                    title={item.label}
                    className={[
                      'nav-item',
                      `mobile-${mobileSlot}`,
                      route.id === item.id && !item.soon ? 'active' : '',
                      blocked ? 'disabled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-disabled={blocked ? 'true' : undefined}
                    onClick={blocked ? (event) => event.preventDefault() : handleNav}
                    onMouseEnter={blocked ? undefined : () => handlePrefetch(item.id)}
                    onFocus={blocked ? undefined : () => handlePrefetch(item.id)}
                  >
                    <NavIcon name={item.icon} />
                    <span className="nav-item-label">
                      <span className="nav-label-desktop">{item.label}</span>
                      <span className="nav-label-mobile">{mobileNavLabel(item)}</span>
                    </span>
                    {item.soon ? <span className="soon-badge">{t('nav.soon')}</span> : null}
                    {locked ? <span className="soon-badge">{t('nav.locked')}</span> : null}
                  </a>
                );
              })}
            </div>
          ))}
          <button
            className={`mobile-more-btn${isMobileMoreActive(route.id) || mobileMoreOpen ? ' active' : ''}`}
            type="button"
            aria-label={t('nav.more')}
            aria-expanded={mobileMoreOpen}
            onClick={() => setMobileMoreOpen((open) => !open)}
          >
            <NavIcon name="more" />
            <span className="mobile-more-btn-label">{t('nav.more')}</span>
          </button>
        </nav>
      </aside>

      <div
        className={`mobile-more-backdrop${mobileMoreOpen ? ' open' : ''}`}
        role="presentation"
        onClick={() => setMobileMoreOpen(false)}
      />
      <aside
        className={`mobile-more-sheet${mobileMoreOpen ? ' open' : ''}`}
        aria-hidden={!mobileMoreOpen}
        inert={!mobileMoreOpen}
      >
        <div className="mobile-more-handle" aria-hidden="true" />
        <div className="mobile-more-head">
          <div>
            <h2>{t('nav.more')}</h2>
            <p>{loading ? t('nav.loading_account') : user?.email || t('nav.signed_out')}</p>
          </div>
          {accessBadge && <span className={`badge ${accessBadgeClass}`}>{accessBadge}</span>}
        </div>
        <div className="mobile-more-list">
          {moreNavItems(nav).map((item) => {
            const blocked = isBlocked(item);
            return (
              <a
                key={item.label}
                className={[
                  'mobile-more-item',
                  route.id === item.id ? 'active' : '',
                  blocked ? 'disabled' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                href={item.href}
                aria-disabled={blocked ? 'true' : undefined}
                onClick={
                  blocked
                    ? (event) => event.preventDefault()
                    : (event) => {
                        setMobileMoreOpen(false);
                        handleNav(event);
                      }
                }
                onMouseEnter={blocked ? undefined : () => handlePrefetch(item.id)}
                onFocus={blocked ? undefined : () => handlePrefetch(item.id)}
              >
                <NavIcon name={item.icon} />
                <span>{item.label}</span>
              </a>
            );
          })}
          <a
            className="mobile-more-item"
            href="https://docs.antigma.ai/antix/introduction"
            target="_blank"
            rel="noreferrer"
          >
            <NavIcon name="docs" />
            <span>{t('dashboard.documentation')}</span>
          </a>
          <LanguageSwitch surface="mobile" />
          <button
            className="mobile-more-item"
            type="button"
            onClick={() => setThemeMode(nextThemeMode(themeMode))}
          >
            <ThemeIcon mode={themeMode} />
            <span>{t('nav.theme_label', { label: themeModeLabel(themeMode) })}</span>
          </button>
          <button className="mobile-more-item danger" type="button" onClick={signOut}>
            <NavIcon name="signout" />
            <span>{t('nav.sign_out')}</span>
          </button>
        </div>
      </aside>

      <header className="topbar">
        <h1 className="topbar-title">{route.title}</h1>
        <CommandPalette pages={paletteDestinations} />
        <div className="topbar-right">
          {user && balance !== undefined ? (
            <a
              className={`topbar-balance${balanceLow ? ' is-low' : ''}`}
              href="/usage"
              onClick={handleNav}
              data-ui-tooltip={text(
                '余额 · 点击充值',
                'Balance · click to add credits',
                '残高 · クリックでチャージ',
              )}
              data-ui-tooltip-placement="bottom"
            >
              <span className="topbar-balance-label">{text('余额', 'Balance', '残高')}</span>
              <span className="topbar-balance-value">
                {balance === null ? t('dashboard.unlimited') : formatUsd(balance)}
              </span>
            </a>
          ) : null}
          <a className="topbar-text-btn" href="https://docs.antigma.ai/antix/introduction">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 7v14" />
              <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
            </svg>
            {t('dashboard.docs')}
          </a>
          <div ref={accountMenuRef} className={`user-menu${menuOpen ? ' open' : ''}`}>
            <button
              className="user-avatar-btn"
              type="button"
              aria-label={
                loading
                  ? t('nav.account_menu')
                  : text(
                      `${user?.email || '未登录'}的账号菜单`,
                      `Account menu for ${user?.email || 'signed out'}`,
                      `${user?.email || 'サインアウト済み'} のアカウントメニュー`,
                    )
              }
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="user-avatar" id="user-menu-email">
                {avatarUrl && !avatarFailed ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  avatarFallback
                )}
              </span>
            </button>
            <div className="user-menu-panel" role="menu" aria-hidden={!menuOpen} inert={!menuOpen}>
              <div className="user-menu-identity">
                <div className="user-menu-email">
                  {loading ? t('nav.loading_account') : user?.email || t('nav.signed_out')}
                </div>
                {accessBadge && (
                  <span className={`badge ${accessBadgeClass} user-menu-badge`}>{accessBadge}</span>
                )}
              </div>
              <div className="user-menu-divider" />
              <div className="user-menu-pref">
                <span className="user-menu-pref-label">{t('common.language')}</span>
                <div className="user-menu-segment" role="group" aria-label={t('common.language')}>
                  {(
                    [
                      ['en', 'EN', 'English'],
                      ['zh-CN', '中', '中文'],
                      ['ja', '日', '日本語'],
                    ] as Array<[Language, string, string]>
                  ).map(([code, label, name]) => (
                    <button
                      key={code}
                      type="button"
                      className={language === code ? 'is-active' : ''}
                      aria-pressed={language === code}
                      aria-label={name}
                      lang={code}
                      onClick={() => void setLanguage(code)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="user-menu-pref">
                <span className="user-menu-pref-label">{text('主题', 'Theme', 'テーマ')}</span>
                <div
                  className="user-menu-segment"
                  role="group"
                  aria-label={text('主题', 'Theme', 'テーマ')}
                >
                  {(['light', 'dark', 'system'] as ThemeMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={themeMode === mode ? 'is-active' : ''}
                      aria-pressed={themeMode === mode}
                      aria-label={themeModeLabel(mode)}
                      data-ui-tooltip={themeModeLabel(mode)}
                      onClick={() => setThemeMode(mode)}
                    >
                      <ThemeGlyph mode={mode} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="user-menu-divider" />
              <button className="user-menu-item" type="button" role="menuitem" onClick={signOut}>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <path d="M16 17l5-5-5-5" />
                  <path d="M21 12H9" />
                </svg>
                {t('nav.sign_out')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="content">
        <div ref={contentRef} className="content-inner">
          <PromotionBanner enabled={Boolean(user)} />
          {user && route.id !== 'usage' ? <BalanceNotice user={user} /> : null}
          {SHELL_TITLED_ROUTES.has(route.id) ? (
            <section className="welcome-section page-title-section">
              <h2 className="welcome-title">{route.title}</h2>
            </section>
          ) : null}
          {children}
        </div>
      </main>
    </>
  );
}

async function signOut() {
  await fetch('/v2/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined);
  window.location.replace('/login');
}

function ThemeIcon({ mode }: { mode: ThemeMode }) {
  return (
    <span className="theme-icon-stack" aria-hidden="true">
      {(['system', 'light', 'dark'] as const).map((value) => (
        <span className={`theme-icon-layer${value === mode ? ' active' : ''}`} key={value}>
          <ThemeGlyph mode={value} />
        </span>
      ))}
    </span>
  );
}

function ThemeGlyph({ mode }: { mode: ThemeMode }) {
  if (mode === 'light') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2" />
        <path d="M12 20v2" />
        <path d="m4.93 4.93 1.41 1.41" />
        <path d="m17.66 17.66 1.41 1.41" />
        <path d="M2 12h2" />
        <path d="M20 12h2" />
        <path d="m6.34 17.66-1.41 1.41" />
        <path d="m19.07 4.93-1.41 1.41" />
      </svg>
    );
  }
  if (mode === 'dark') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 3a6 6 0 0 0 9 7.5A9 9 0 1 1 12 3" />
      </svg>
    );
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );
}

function themeModeLabel(mode: ThemeMode) {
  if (mode === 'light') return t('nav.light');
  if (mode === 'dark') return t('nav.dark');
  return t('nav.system');
}

// A $0 account can open every page, so say plainly why its requests will fail
// and where to fund it. Hidden on /usage, which carries the top-up itself.
function BalanceNotice({ user }: { user: PortalUser }) {
  const balance = user.credit_balance_usd;
  if (isSuperAdmin(user) || balance === null || balance === undefined || balance > 0) {
    return null;
  }
  return (
    <FeedbackNotice
      tone="info"
      placement="page"
      className="balance-notice"
      title={text('余额为 $0', 'Your balance is $0', '残高は $0 です')}
      message={
        user.billing_enabled
          ? text(
              '充值后即可开始发送请求。',
              'Top up to start making requests.',
              'チャージするとリクエストを送信できます。',
            )
          : text(
              '账户有余额后才能发送请求。',
              'Requests will fail until your account has a balance.',
              'アカウントに残高があるまでリクエストは失敗します。',
            )
      }
      action={
        user.billing_enabled
          ? { label: text('充值', 'Top up', 'チャージ'), onClick: () => navigate('/usage') }
          : undefined
      }
    />
  );
}

function handleNav(event: MouseEvent<HTMLAnchorElement>) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const href = event.currentTarget.getAttribute('href');
  if (!href || href.startsWith('http') || href === '#') return;
  event.preventDefault();
  navigate(href);
}

function handlePrefetch(id: Route['id']) {
  if (id === 'endpoints') {
    prefetch.endpoints();
  } else if (id === 'team') {
    prefetch.organizations();
  } else if (id === 'security') {
    prefetch.passkeys();
  } else if (id === 'models') {
    prefetch.availableModels();
    prefetch.modelPricing();
  }
}

function mobileNavSlot(item: NavItem): MobileSlot {
  if (item.soon) return 'hidden';
  return mobilePrimaryRoutes.has(item.id) ? 'primary' : 'more';
}

function isMobileMoreActive(id: Route['id']) {
  return id !== 'login' && !mobilePrimaryRoutes.has(id);
}

function moreNavItems(navItems: Array<{ title: string; items: NavItem[] }>) {
  return navItems.flatMap((section) =>
    section.items.filter((item) => mobileNavSlot(item) === 'more'),
  );
}

function mobileNavLabel(item: NavItem) {
  if (item.id === 'home') return t('nav.home');
  if (item.id === 'keys') return t('nav.keys');
  if (item.id === 'usage') return t('nav.usage');
  return item.label;
}

function toggleSidebar() {
  const collapsed = document.documentElement.dataset.sidebar === 'collapsed';
  if (collapsed) {
    delete document.documentElement.dataset.sidebar;
    localStorage.setItem('antix_sidebar', 'expanded');
  } else {
    document.documentElement.dataset.sidebar = 'collapsed';
    localStorage.setItem('antix_sidebar', 'collapsed');
  }
}

function NavIcon({ name }: { name: NavItem['icon'] | 'more' | 'docs' | 'signout' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {navIconChildren[name]}
    </svg>
  );
}

const navIconChildren: Record<NavItem['icon'] | 'more' | 'docs' | 'signout', ReactNode> = {
  dashboard: (
    <>
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="16" rx="1" />
    </>
  ),
  organization: (
    <>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  security: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.92a1 1 0 0 1-.68 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  endpoints: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </>
  ),
  keys: (
    <>
      <path d="m15.5 7.5 2.3 2.3a1 1 0 0 0 1.4 0l2.1-2.1a1 1 0 0 0 0-1.4L19 4" />
      <path d="m21 2-9.6 9.6" />
      <circle cx="7.5" cy="15.5" r="5.5" />
    </>
  ),
  models: (
    <>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </>
  ),
  usage: (
    <>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </>
  ),
  support: (
    <>
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      <path d="M8 9h8" />
      <path d="M8 13h5" />
    </>
  ),
  limits: (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </>
  ),
  docs: (
    <>
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </>
  ),
  signout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
};
